/**
 * Pure utility — derives RelatedNode[] from graph data for the memory node panel.
 *
 * Handles the D3 mutation where react-force-graph-2d replaces source/target
 * string IDs with resolved node object references at runtime.
 *
 * No browser dependencies — safe to import in Vitest unit tests.
 */
import type {
  ForceGraphNode,
  ForceGraphLink,
  RelatedNode,
} from '@/workshop/components/shapes/memory-frame/types';
import { GRAPH_NODE_COLORS } from '@/workshop/components/shapes/memory-frame/types';

/** Handles D3's runtime string→object mutation on link source/target. */
function resolveId(ref: string | { id: string }): string {
  return typeof ref === 'string' ? ref : ref.id;
}

/**
 * Returns all RelatedNode entries for a given node, derived from the graph links.
 * Confidence is taken from ForceGraphLink.value (0.0–1.0).
 */
export function deriveRelatedNodes(
  nodeId: string,
  links: ForceGraphLink[],
  nodes: ForceGraphNode[],
): RelatedNode[] {
  return links
    .filter(
      l =>
        resolveId(l.source as string | { id: string }) === nodeId ||
        resolveId(l.target as string | { id: string }) === nodeId,
    )
    .map(l => {
      const srcId = resolveId(l.source as string | { id: string });
      const isOutgoing = srcId === nodeId;
      const connectedId = isOutgoing
        ? resolveId(l.target as string | { id: string })
        : srcId;
      const connectedNode = nodes.find(n => n.id === connectedId);
      return {
        id: connectedId,
        name: connectedNode?.label ?? connectedId,
        type: connectedNode?.type ?? 'concept',
        color: connectedNode?.color ?? GRAPH_NODE_COLORS['concept'],
        relationship: l.type.replace(/_/g, ' '),
        direction: isOutgoing ? 'outgoing' : 'incoming',
        confidence: l.value,
      };
    });
}
